import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * The three lessons that open Algebra I.
 *
 * None of them introduces a new operation. Each takes something the student has
 * been doing since Grade 7 and asks a question about it that has not been asked
 * before: what if the numbers in this equation are letters; what if a function
 * refuses some inputs; what if the answer to an inequality is a region rather
 * than a stretch of a line.
 *
 * That is deliberate. Algebra I is the year in which the *object* changes —
 * from a number you are looking for to an expression you are reshaping — and
 * these three make the change on ground the student already knows.
 */
export const ALGEBRA_FOUNDATION_LESSONS: Lesson[] = [
  lesson({
    id: 'literal-equations-01',
    unitId: 'unit-algebra-foundations',
    conceptId: 'literal-equations',
    title: 'Rearranging Formulas',
    summary: 'Solving for the letter you want, when every other letter is a number you were not given.',
    objectives: [
      'Solve a formula for any one of its letters',
      'Undo operations in reverse order when the numbers are letters',
      'Divide a whole side rather than a single term',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'A formula is an equation whose numbers you were not told',
        blocks: [
          text('The area of a triangle is a formula everyone meets early:'),
          math('A = \\tfrac{1}{2}bh'),
          text('It answers one question: given a base and a height, what is the area? But the question that arrives in practice is often the other one. A triangular flower bed must cover 12 m², and the base is fixed at 6 m — how deep does it need to be?'),
          text('You could substitute the numbers and then solve. Rearranging first solves it once, for every set of numbers there will ever be:'),
          math('h = \\frac{2A}{b}'),
          insight('Nothing new happened there. It is the same solving you have done since Grade 7, on an equation whose other numbers simply have not been told to you yet.'),
        ],
      },
      intuition: {
        title: 'The same moves, with letters instead of numbers',
        blocks: [
          text('Solving 3x = 12 means undoing a multiplication by 3, so you divide by 3. Solving bx = A means undoing a multiplication by b, so you divide by b. The letter changes nothing: what matters is what the letter is *doing*.'),
          table(['If the letter is…', 'Remove it by…', 'Example'], [
            ['multiplying', 'dividing both sides', 'bh = 2A → h = 2A/b'],
            ['dividing', 'multiplying both sides', 'd/t = s → d = st'],
            ['added', 'subtracting from both sides', 'P = 2l + 2w → P − 2l = 2w'],
            ['squared', 'taking the root of both sides', 'A = πr² → r = √(A/π)'],
          ]),
          text('And the order is the order it has always been — undo the outermost operation first, which is the *last* thing that would have happened to the letter you want.'),
          warning('Dividing applies to the whole side, never to the term nearest the sign. From P = 2l + 2w, dividing by 2 gives P/2 = l + w, not P/2 = 2l + w. The subtraction has to happen first, which is why it usually does.', 'A side, not a term'),
          insight('There is one honest difference from numerical solving. You cannot tidy the answer at the end, because there is nothing to compute. An expression like 2A/b *is* the finished answer, and looking unfinished is not the same as being unfinished.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('One formula, three rearrangements. Each isolates a different letter, and all three say the same thing:'),
          table(['Solved for', 'Result', 'The question it answers'], [
            ['d', 'd = st', 'How far, at this speed, for this long?'],
            ['s', 's = d/t', 'How fast, to cover this in this time?'],
            ['t', 't = d/s', 'How long, to cover this at this speed?'],
          ]),
          text('A formula is not a one-way instruction. It is a statement about how three quantities are related, and any one of them is allowed to be the unknown.'),
          tip('Check a rearrangement with numbers you know fit. If d = 100 and t = 4 give s = 25, then substituting into your rearranged version has to return 4 — and a version that returns anything else is wrong, whatever it looks like.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('The perimeter of a rectangle is P = 2l + 2w. Solve for w.'),
          steps([
            { label: 'The w sits inside 2w, which is added to 2l. Undo the addition first.', latex: 'P - 2l = 2w' },
            { label: 'Now divide the whole side by 2.', latex: '\\frac{P - 2l}{2} = w' },
            { label: 'Write it with w on the left.', latex: 'w = \\frac{P - 2l}{2}' },
            { label: 'Check on a rectangle you know: l = 5, w = 3, so P = 16.', latex: '\\frac{16 - 10}{2} = 3' },
          ]),
          text('The bracket in the numerator is not decoration. Without it the expression reads P − 2l/2, which halves only the second term and is a different quantity entirely.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('literal-equations', 4)] },
      independent: { title: 'On your own', blocks: [practice('literal-equations', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('literal-equations', 3, 'Formulas where the letter you want is buried inside something else.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Rearranging a formula is ordinary solving; the other numbers are simply unnamed.',
            'Undo what the letter is doing: multiplying → divide, dividing → multiply.',
            'Undo the outermost operation first.',
            'Dividing applies to the entire side, so subtract before you divide.',
            'An answer that cannot be computed any further is finished.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'domain-range-01',
    unitId: 'unit-algebra-foundations',
    conceptId: 'domain-and-range',
    title: 'Domain and Range',
    summary: 'Which inputs a function accepts, and which outputs it can actually produce.',
    objectives: [
      'State the domain and range of a function from a graph, a table or a rule',
      'Find the inputs a rule has to exclude',
      'Read the range of a parabola from its vertex',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'The question nobody has asked yet',
        blocks: [
          text('For a year now you have been feeding numbers into functions and reading what comes out. Here is a function that refuses one of them:'),
          math('f(x) = \\frac{1}{x - 3}'),
          text('Try x = 3. The denominator is zero, and the machine has nothing to hand back — not a large number, not zero, nothing at all. The input is simply not allowed.'),
          insight('**Domain** is the set of inputs a function accepts. **Range** is the set of outputs it can produce. Every function has both, and until now they have quietly been "all the numbers" every single time.'),
        ],
      },
      intuition: {
        title: 'In and out',
        blocks: [
          text('Keep the machine picture. The domain is what you are allowed to put in the hopper; the range is everything that can come out of the chute.'),
          text('Most rules accept everything, which is exactly why the exceptions are worth learning by name. At this level there are only two:'),
          list([
            '**A denominator may not be zero.** Set the bottom equal to zero, solve, and exclude whatever you find.',
            '**A square root may not take a negative.** Set the inside greater than or equal to zero, and solve that inequality.',
          ], true),
          math('g(x) = \\sqrt{x - 4} \\quad\\Longrightarrow\\quad x - 4 \\ge 0 \\quad\\Longrightarrow\\quad x \\ge 4'),
          text('Range is the harder of the two, because it asks what the function *can* produce rather than what it will accept — and answering it usually means knowing the shape of the graph.'),
          insight('For y = x², every output is a square, so no output is negative: the range is y ≥ 0. The domain meanwhile is every real number — you may square anything you like. Domain and range are genuinely different questions, and a parabola is where that first becomes impossible to miss.'),
          warning('A parabola turns around, and its range stops where it turns. Reporting "all real numbers" for the range of y = x² − 4 throws away the whole meaning of the vertex: nothing below −4 is ever produced, by any input whatsoever.', 'The vertex is a floor'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('On a graph, the domain is what you see sweeping left to right, and the range is what you see sweeping bottom to top:'),
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              transform: { k: -4 },
              min: -6,
              max: 6,
              points: [{ x: 0, y: -4, label: 'vertex' }],
            },
            'y = x² − 4. Sweeping left to right, the curve sits above every x, so the domain is all real numbers. Sweeping bottom to top, nothing exists below −4, so the range is y ≥ −4.',
          ),
          table(['Function', 'Domain', 'Range'], [
            ['y = 2x + 1', 'all real numbers', 'all real numbers'],
            ['y = x²', 'all real numbers', 'y ≥ 0'],
            ['y = x² − 4', 'all real numbers', 'y ≥ −4'],
            ['y = 1/(x − 3)', 'x ≠ 3', 'y ≠ 0'],
            ['y = √(x − 4)', 'x ≥ 4', 'y ≥ 0'],
          ]),
          tip('The last two rows are worth staring at. A restriction on the input has nothing to do with a restriction on the output, and a function can easily carry one without the other.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('State the domain of f(x) = √(2x − 10), and the range of g(x) = (x − 1)² + 3.'),
          steps([
            { label: 'For f, the inside of the root may not be negative.', latex: '2x - 10 \\ge 0' },
            { label: 'Solve that inequality.', latex: 'x \\ge 5' },
            { label: 'For g, a squared bracket is never negative, so its smallest value is zero.', latex: '(x - 1)^{2} \\ge 0' },
            { label: 'Add the 3, and the smallest possible output is 3.', latex: 'g(x) \\ge 3' },
          ]),
          text('Notice that g accepts every input — nothing is excluded — while producing only outputs of 3 and above. Domain unrestricted, range restricted. Answering one of these questions does not answer the other.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('domain-and-range', 4)] },
      independent: { title: 'On your own', blocks: [practice('domain-and-range', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('domain-and-range', 3, 'Rules that exclude an input, and curves that stop at a vertex.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Domain is the inputs allowed; range is the outputs produced.',
            'A denominator may not be zero — exclude whatever makes it zero.',
            'A square root may not take a negative — solve inside ≥ 0.',
            'A parabola’s range starts, or stops, at the y-value of its vertex.',
            'A restriction on the domain implies nothing at all about the range.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'two-variable-inequalities-01',
    unitId: 'unit-algebra-foundations',
    conceptId: 'two-variable-inequalities',
    title: 'Linear Inequalities in Two Variables',
    summary: 'A boundary line and the half of the plane on one side of it — and how to tell which half.',
    objectives: [
      'Decide whether an ordered pair satisfies an inequality',
      'Choose a solid or a dashed boundary line',
      'Use a test point to decide which side to shade',
      'Read the solution of a system of inequalities as an overlap',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'From a ray to a region',
        blocks: [
          text('In Grade 7, x > 2 was answered by shading a piece of the number line. Give the same inequality a second variable and the answer gains a dimension:'),
          math('y > 2x - 1'),
          text('There is no list of answers to write down. (0, 0) works, since 0 > −1. (0, −5) does not. (3, 5) does not either, because 5 is not greater than 5. What the solutions form is a *region* — everything on one side of a particular line.'),
          insight('An equation in two variables draws a line. An inequality in two variables shades one side of it. That is the whole idea; everything else is bookkeeping about which side, and whether the line itself counts.'),
        ],
      },
      intuition: {
        title: 'Boundary, then side',
        blocks: [
          text('Every one of these is drawn in two moves.'),
          list([
            '**Draw the boundary.** Replace the inequality sign with = and graph that line as you would any other. Make it **dashed** for < or >, and **solid** for ≤ or ≥.',
            '**Choose a side.** Take any point not on the line, put it into the original inequality, and see whether the statement comes out true. If it does, shade that point’s side; if it does not, shade the other one.',
          ], true),
          insight('Use (0, 0) as the test point whenever the line misses it. The arithmetic collapses to almost nothing, which leaves the decision itself as the only thing that can go wrong.'),
          warning('Do not read the side off the inequality sign. "Greater than" means "above" only once y is alone on the left, and rearranging can flip it: from −y > x, dividing by −1 reverses the sign to y < −x, and the region is *below* a line that the original inequality appeared to put you above.', 'The sign is not the side'),
          warning('A test point taken from the boundary itself decides nothing. It makes the two sides equal, which is true of every point on the line and says nothing whatever about either half.', 'Test off the line'),
          text('The dashed-versus-solid distinction is the open circle from the number line, drawn in two dimensions. The boundary is where the two sides are exactly equal, so it belongs to the solution only when the inequality permits equality.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -6,
              max: 6,
              lines: [{ slope: 2, intercept: -1, label: 'y = 2x − 1', dashed: true, shade: 'above' }],
              points: [{ x: 0, y: 0, label: 'test' }],
            },
            'y > 2x − 1. The boundary is dashed because the inequality is strict, and (0, 0) satisfies it — so the shaded half is the one holding the origin.',
          ),
          text('Two inequalities at once shade twice, and the answer is where the shadings overlap. That overlap is exactly the set of points obeying both, which is what a **system** of inequalities asks for:'),
          figure(
            {
              kind: 'coordinate-grid',
              min: -6,
              max: 6,
              lines: [
                { slope: 1, intercept: 1, label: 'y ≤ x + 1', shade: 'below' },
                { slope: -1, intercept: 3, label: 'y ≤ −x + 3', shade: 'below' },
              ],
            },
            'Both boundaries are solid, so both lines are included. The solutions of the system are the points lying under both — the wedge where the two shaded halves agree.',
          ),
          tip('A point in the overlap has to satisfy *both* inequalities when you check it. Checking only one is how a system quietly turns back into a single inequality.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Graph 3x + y ≥ 6, and decide whether (1, 2) is a solution.'),
          steps([
            { label: 'Rearrange so y stands alone. Adding and subtracting never flip the sign.', latex: 'y \\ge -3x + 6' },
            { label: 'The boundary passes through (0, 6) with slope −3, drawn solid because equality is allowed.', latex: 'y = -3x + 6' },
            { label: 'Test (0, 0) in the original inequality.', latex: '3(0) + 0 = 0 \\ge 6 \\text{ is false}' },
            { label: 'So shade the side away from the origin — above the line.', latex: 'y \\ge -3x + 6' },
            { label: 'Now test the point actually asked about.', latex: '3(1) + 2 = 5 \\ge 6 \\text{ is false}' },
          ]),
          text('(1, 2) is not a solution. Notice that it was tested in the inequality rather than read off the picture: a point lying close to the boundary is exactly where reading off a graph goes wrong.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('two-variable-inequalities', 4)] },
      independent: { title: 'On your own', blocks: [practice('two-variable-inequalities', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('two-variable-inequalities', 3, 'Inequalities needing rearrangement, and systems with an overlap.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The solutions form a region: one side of a boundary line.',
            'Dashed boundary for < and >, solid for ≤ and ≥.',
            'Decide the side with a test point, never with the inequality sign.',
            '(0, 0) is the easiest test point whenever the line misses it.',
            'A system is solved by the overlap, and a solution must satisfy every inequality in it.',
          ]),
        ],
      },
    },
  }),
];
